import { getSlugFromPage } from '../../utils/routeUtils';
import { getMetaOAuthRedirectUri } from './metaSDK';

const APP_ID = import.meta.env.VITE_FACEBOOK_APP_ID || '';
const CONFIG_ID = import.meta.env.VITE_WHATSAPP_EMBEDDED_SIGNUP_CONFIG_ID || '';
const GRAPH_VERSION = 'v21.0';
const RETURN_STATE = 'adlitics_whatsapp';
const RETURN_KEY = 'adlitics_whatsapp_signup_return';

export type CoexistenceSignup = {
  code: string;
  redirectUri: string;
};

export type WhatsappSignupReturn =
  | { ok: true; signup: CoexistenceSignup }
  | { ok: false; message: string };

function requirePublicConfig(): void {
  if (!APP_ID) throw new Error('App da Meta não configurado (VITE_FACEBOOK_APP_ID).');
  if (!CONFIG_ID) {
    throw new Error(
      'Crie uma configuração Embedded Signup no app da Meta (WhatsApp) e defina VITE_WHATSAPP_EMBEDDED_SIGNUP_CONFIG_ID.',
    );
  }
}

/** URI já aceita no Login do Facebook. A página de canais não está nessa lista e a Meta bloqueia. */
function redirectUri(): string {
  return getMetaOAuthRedirectUri().replace(/\/$/, '');
}

function channelsUrl(): string {
  return `${window.location.origin}/${getSlugFromPage('connections')}`.replace(/\/$/, '');
}

function dialogUrl(uri: string): string {
  const params = new URLSearchParams({
    client_id: APP_ID,
    config_id: CONFIG_ID,
    redirect_uri: uri,
    response_type: 'code',
    override_default_response_type: 'true',
    state: RETURN_STATE,
    extras: JSON.stringify({
      setup: {},
      featureType: 'whatsapp_business_app_onboarding',
      sessionInfoVersion: '3',
    }),
  });
  return `https://www.facebook.com/${GRAPH_VERSION}/dialog/oauth?${params}`;
}

function captureSignupReturn(): void {
  const params = new URLSearchParams(window.location.search);
  if (params.get('state') !== RETURN_STATE) return;

  const code = params.get('code')?.trim() || '';
  const error = params.get('error') || '';
  sessionStorage.setItem(RETURN_KEY, JSON.stringify({
    code,
    error,
    redirectUri: redirectUri(),
  }));

  ['code', 'state', 'error', 'error_reason', 'error_description'].forEach((key) => params.delete(key));
  const search = params.toString();
  const here = `${window.location.origin}${window.location.pathname}`.replace(/\/$/, '');
  window.history.replaceState({}, '', `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`);
  if (here !== channelsUrl()) window.location.replace(channelsUrl());
}

captureSignupReturn();

export function takeWhatsappSignupReturn(): WhatsappSignupReturn | null {
  const raw = sessionStorage.getItem(RETURN_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(RETURN_KEY);

  let stored: { code?: string; error?: string; redirectUri?: string };
  try {
    stored = JSON.parse(raw) as { code?: string; error?: string; redirectUri?: string };
  } catch {
    return { ok: false, message: 'A Meta não devolveu a autorização. Conecte de novo.' };
  }

  if (stored.error === 'access_denied') {
    return { ok: false, message: 'Conexão cancelada no WhatsApp Business.' };
  }
  if (!stored.code || stored.code.length < 8 || stored.code.length > 1024 || !stored.redirectUri) {
    return {
      ok: false,
      message: 'A Meta não devolveu a autorização. Confira a URI de redirecionamento do Login do Facebook.',
    };
  }
  return { ok: true, signup: { code: stored.code, redirectUri: stored.redirectUri } };
}

/** Sai desta página e abre o cadastro da Meta. O script do Facebook está bloqueado pela CSP. */
export function startWhatsappEmbeddedSignup(): void {
  requirePublicConfig();
  window.location.assign(dialogUrl(redirectUri()));
}
